/**
 * @fileoverview The third-party AI disclosure an instructor accepts before
 * Hans sends lesson audio to its AI processors (App Review guideline 5.1.2(i)).
 *
 * The server owns the text and its version: `GET /auth/ai-disclosure` returns
 * the current disclosure, `POST /auth/ai-disclosure/accept` records an explicit
 * agreement to one version, and `/auth/me` carries
 * `ai_disclosure: {current_version, accepted_version}` so a client can tell
 * whether to ask before opening the microphone. When the runtime flag
 * AI_DISCLOSURE_REQUIRED is on, start-upload refuses with a 403
 * `ai_disclosure_required` until the current version is accepted.
 *
 * This is separate from guardian recording consent (the consent ledger). It is
 * the instructor's own permission for the AI processing, not the family's.
 */

import api from "@/lib/api";

/** The 403 code start-upload returns while the disclosure is unaccepted. */
export const AI_DISCLOSURE_REQUIRED = "ai_disclosure_required";
/** The 409 code the accept endpoint returns for a stale version. */
export const AI_DISCLOSURE_VERSION_MISMATCH = "ai_disclosure_version_mismatch";

const detailCode = (error) => {
  const detail = error?.response?.data?.detail;
  return detail && typeof detail === "object" ? detail.code : undefined;
};

/**
 * Whether the instructor must see the disclosure before recording.
 *
 * An absent `ai_disclosure` means an older backend that has no disclosure, and
 * must never block a recording, so it reads as "no".
 */
export function needsAiDisclosure(user) {
  const state = user?.ai_disclosure;
  if (!state || typeof state !== "object" || !state.current_version) return false;
  return state.accepted_version !== state.current_version;
}

/** Whether an upload failed because the disclosure has not been accepted. */
export function isAiDisclosureRequiredError(error) {
  return error?.response?.status === 403 && detailCode(error) === AI_DISCLOSURE_REQUIRED;
}

/** Whether an accept failed because the disclosure changed since it was read. */
export function isAiDisclosureVersionMismatch(error) {
  return error?.response?.status === 409 && detailCode(error) === AI_DISCLOSURE_VERSION_MISMATCH;
}

/** Read the current disclosure: {version, title, paragraphs, providers, accepted_version, accepted_at}. */
export async function fetchAiDisclosure(options) {
  const { data } = await api.get("/auth/ai-disclosure", options);
  return data;
}

/** Record agreement to one version. Resolves to {accepted_version, accepted_at}. */
export async function acceptAiDisclosure(version) {
  const { data } = await api.post("/auth/ai-disclosure/accept", { version });
  return data;
}

/**
 * The cached user with an acceptance applied, so the gate stays open even if
 * the follow-up /auth/me refresh fails. Returns the user unchanged when it
 * carries no disclosure state.
 */
export function withAcceptedAiDisclosure(user, acceptedVersion) {
  if (!user?.ai_disclosure || !acceptedVersion) return user;
  // The server accepts only its current version, so both fields now name it.
  return {
    ...user,
    ai_disclosure: { ...user.ai_disclosure, current_version: acceptedVersion, accepted_version: acceptedVersion },
  };
}
